import { useEffect, useState, useRef } from "react";
import { Search, Plus, FileText, Eye, Download, XCircle, ChevronLeft, ChevronRight } from "lucide-react";
import debitNoteService from "../services/debitNoteService";
import CreateDebitNoteModal from "../components/CreateDebitNoteModal";
import DebitNoteDetailModal from "../components/DebitNoteDetailModal";
import Swal from "sweetalert2";

export default function DebitNotes() {
  const [debitNotes, setDebitNotes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDebitNote, setSelectedDebitNote] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);

  const fetchDebitNotes = async (page = 1, searchTerm = search) => {
    try {
      setLoading(true);
      const res = await debitNoteService.getAll({ page, search: searchTerm });
      if (res.data) {
        setDebitNotes(res.data);
        setCurrentPage(res.current_page);
        setTotalPages(res.last_page);
        setTotal(res.total);
        setFrom(res.from);
        setTo(res.to);
      } else {
        setDebitNotes(Array.isArray(res) ? res : []);
      }
    } catch (error) {
      Swal.fire("Error", "No se pudieron cargar las notas de débito", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDebitNotes();
    setTimeout(() => searchRef.current?.focus(), 100);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) fetchDebitNotes(newPage, search);
  };

  const handleSearch = (value) => {
    setSearch(value);
    clearTimeout(window.searchTimeoutDN);
    window.searchTimeoutDN = setTimeout(() => {
      setCurrentPage(1);
      fetchDebitNotes(1, value);
    }, 500);
  };

  const handleViewDetail = async (debitNote) => {
    try {
      const res = await debitNoteService.getById(debitNote.id);
      setSelectedDebitNote(res);
      setShowDetailModal(true);
    } catch (error) {
      Swal.fire("Error", "No se pudo cargar el detalle", "error");
    }
  };

  const handleDownloadPdf = async (debitNote) => {
    try {
      await debitNoteService.downloadPdf(debitNote.id, debitNote.numero_control);
    } catch (error) {
      Swal.fire("Error", "No se pudo descargar el PDF", "error");
    }
  };

  const handleCancel = async (debitNote) => {
    const res = await Swal.fire({
      title: "¿Cancelar Nota de Débito?",
      html: `<p><strong>Número de Control:</strong> ${debitNote.numero_control}</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No",
    });

    if (res.isConfirmed) {
      try {
        await debitNoteService.cancel(debitNote.id);
        Swal.fire("Cancelada", "Nota de débito cancelada correctamente", "success");
        fetchDebitNotes(currentPage, search);
      } catch (error) {
        Swal.fire("Error", error.response?.data?.error || "No se pudo cancelar", "error");
      }
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <FileText size={28} color="#2563eb" />
            <h1 className="card-title">Notas de Débito (DTE 06)</h1>
          </div>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} /> Nueva Nota de Débito
          </button>
        </div>

        <div className="card-body">
          <div className="form-group" style={{ position: "relative", marginBottom: "20px" }}>
            <Search size={18} style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#a0aec0" }} />
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por cliente, número de control o código de generación..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              ref={searchRef}
              style={{ paddingLeft: "44px" }}
            />
          </div>

          {loading ? (
            <div className="text-center" style={{ padding: "40px" }}>
              <div className="spinner" style={{ margin: "0 auto" }}></div>
              <p style={{ marginTop: "16px", color: "#718096" }}>Cargando notas de débito...</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Código de Generación</th>
                    <th>Cliente</th>
                    <th>Fecha</th>
                    <th style={{ textAlign: "right" }}>Monto</th>
                    <th style={{ textAlign: "center" }}>Estado DTE</th>
                    <th style={{ textAlign: "center" }}>Estado</th>
                    <th style={{ textAlign: "center" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {debitNotes.length === 0 ? (
                    <tr>
                      <td colSpan="7">
                        <div className="text-center" style={{ padding: "40px 20px" }}>
                          <FileText size={48} color="#cbd5e0" style={{ marginBottom: "16px" }} />
                          <p style={{ color: "#a0aec0", fontSize: "16px" }}>No hay notas de débito registradas</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    debitNotes.map((dn) => (
                      <tr key={dn.id}>
                        <td>
                          <strong style={{ fontSize: "11px", wordBreak: "break-all", fontFamily: "monospace", display: "block", maxWidth: "300px" }}>
                            {dn.codigo_generacion}
                          </strong>
                        </td>
                        <td>{dn.sale?.client?.nombre || "N/A"}</td>
                        <td>{new Date(dn.fecha_emision).toLocaleDateString("es-SV")}</td>
                        <td style={{ textAlign: "right", fontWeight: 700, color: "#2563eb" }}>
                          ${Number(dn.amount).toFixed(2)}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span style={{ padding: "4px 12px", background: dn.estado_dte === "VALIDADO" ? "#d1fae5" : "#fee2e2", color: dn.estado_dte === "VALIDADO" ? "#065f46" : "#991b1b", borderRadius: "6px", fontSize: "12px", fontWeight: 600 }}>
                            {dn.estado_dte === "VALIDADO" ? "✓ Validado" : dn.estado_dte}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span style={{ padding: "4px 12px", background: dn.status === "active" ? "#dbeafe" : "#f3f4f6", color: dn.status === "active" ? "#1e40af" : "#6b7280", borderRadius: "6px", fontSize: "12px", fontWeight: 600 }}>
                            {dn.status === "active" ? "Activa" : "Cancelada"}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2" style={{ justifyContent: "center" }}>
                            <button className="btn btn-primary btn-sm" onClick={() => handleViewDetail(dn)} title="Ver detalle">
                              <Eye size={16} />
                            </button>
                            <button className="btn btn-success btn-sm" onClick={() => handleDownloadPdf(dn)} title="Descargar PDF">
                              <Download size={16} />
                            </button>
                            {dn.status === "active" && (
                              <button className="btn btn-danger btn-sm" onClick={() => handleCancel(dn)} title="Cancelar">
                                <XCircle size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div style={{ marginTop: "24px", display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
              <button className="btn btn-secondary btn-sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                <ChevronLeft size={16} />
              </button>
              <span style={{ padding: "6px 12px", color: "#718096", fontSize: "14px" }}>Página {currentPage} de {totalPages}</span>
              <button className="btn btn-secondary btn-sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateDebitNoteModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); fetchDebitNotes(currentPage, search); }}
        />
      )}

      {showDetailModal && selectedDebitNote && (
        <DebitNoteDetailModal
          debitNote={selectedDebitNote}
          onClose={() => { setShowDetailModal(false); setSelectedDebitNote(null); }}
          onDownloadPdf={handleDownloadPdf}
        />
      )}
    </div>
  );
}